import React, { useState, useEffect } from 'react'
import PayPalPaymentDialog from '@/components/common/PayPalPaymentDialog'
import { MembershipPlan, UpgradeOption } from '@/types'
import { getPaymentMethods } from '@/services/paymentService'
import Toast from '@/components/Toast'
import { useTranslation } from '@/i18n'

// API 基础配置
const API_BASE_URL = (import.meta as any).env?.VITE_API_BASE_URL

// 获取请求头
const getHeaders = () => {
  const token = localStorage.getItem('token') || localStorage.getItem('auth_token') || ''
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  }
}

// 通用请求处理
const handleResponse = async <T,>(response: Response): Promise<T> => {
  if (!response.ok) {
    const errorText = await response.text()
    throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`)
  }

  const data = await response.json()
  console.log('📥 [handleResponse] Full API response:', data)

  // 根据API文档格式处理响应
  if (data.code !== 200) {
    throw new Error(data.message || 'API request failed')
  }

  return data.data as T
}

const MembershipPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'monthly' | 'yearly'>('monthly')
  const [selectedPlan, setSelectedPlan] = useState<MembershipPlan | null>(null)
  const [isPayPalDialogOpen, setIsPayPalDialogOpen] = useState(false)
  const [paymentMethods, setPaymentMethods] = useState<string[]>([])
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null)
  const { t } = useTranslation()

  // 会员计划配置
  const membershipPlans: MembershipPlan[] = [
    {
      id: 1, // 对应API中的基础版
      name: t('membership.subscriptionType.basic'),
      description: t('membership.descriptions.basic'),
      price: activeTab === 'monthly' ? 19.99 : 199.99, // 用户要求的价格
      features: [
        `${activeTab === 'monthly' ? '2000' : '24000'} credits/${activeTab === 'monthly' ? 'month' : 'year'}`, // 用户要求的权益
        t('membership.features.aiGeneratedContent'),
        t('membership.features.voiceovers'),
        t('membership.features.basicEditingTools'),
        t('membership.features.emailSupport'),
        '1GB storage',
        '200 Task Histories'
      ]
    },
    {
      id: 2, // 对应API中的专业版
      name: t('membership.subscriptionType.pro'),
      description: t('membership.descriptions.pro'),
      price: activeTab === 'monthly' ? 69.99 : 699.99, // 用户要求的价格
      features: [
        `${activeTab === 'monthly' ? '12000' : '144000'} credits/${activeTab === 'monthly' ? 'month' : 'year'}`, // 用户要求的权益
        t('membership.features.aiGeneratedContent'),
        t('membership.features.voiceovers'),
        t('membership.features.basicEditingTools'),
        t('membership.features.emailSupport'),
        '10GB storage',
        '1000 Task Histories'
      ],
      recommended: true
    },
    {
      id: 3, // 对应API中的企业版
      name: t('membership.subscriptionType.business'),
      description: t('membership.descriptions.business'),
      price: activeTab === 'monthly' ? 199.99 : 1999.99, // 用户要求的价格
      features: [
        `${activeTab === 'monthly' ? '40000' : '480000'} credits/${activeTab === 'monthly' ? 'month' : 'year'}`, // 用户要求的权益
        t('membership.features.aiGeneratedContent'),
        t('membership.features.voiceovers'),
        t('membership.features.basicEditingTools'),
        t('membership.features.emailSupport'),
        '100GB storage',
        '10000 Task Histories'
      ]
    }
  ]

  // 调试：输出所有计划配置
  console.log('📋 [MembershipPage] All membership plans:', membershipPlans)

  // 当前会员状态数据
  const [membershipStatus, setMembershipStatus] = useState<any>(null)
  const [upgradeOptions, setUpgradeOptions] = useState<UpgradeOption[]>([])
  const [loading, setLoading] = useState(true)

  // 获取支付方式
  useEffect(() => {
    const fetchPaymentMethods = async () => {
      try {
        const methods = await getPaymentMethods()
        // 过滤出可用的支付方式
        const availableMethods = methods.methods
          .filter(method => ['paypal', 'stripe'].includes(method.code))
          .map(method => method.name)
        setPaymentMethods(availableMethods)
      } catch (error) {
        console.error('获取支付方式失败:', error)
      }
    }

    fetchPaymentMethods()
  }, [])

  // 获取当前会员状态
  useEffect(() => {
    const fetchMembershipStatus = async () => {
      try {
        setLoading(true)
        // 调用新的订阅接口获取会员状态
        const response = await fetch(`${API_BASE_URL}/subscriptions`, {
          method: 'GET',
          headers: getHeaders()
        })

        const data = await handleResponse<any>(response)
        console.log('📊 [fetchMembershipStatus] API response data:', data)
        console.log('📊 [fetchMembershipStatus] benefit_periods:', data.active_subscription?.benefit_periods)
        const creditsBenefit = data.active_subscription?.benefit_periods?.find((bp: any) => bp.benefit_code === 'credits')
        console.log('📊 [fetchMembershipStatus] credits benefit:', creditsBenefit)
        if (creditsBenefit) {
          console.log('💡 remaining_value 计算验证:', {
            total_value: creditsBenefit.total_value,
            used_value: creditsBenefit.used_value,
            remaining_value: creditsBenefit.remaining_value,
            验证: creditsBenefit.total_value - creditsBenefit.used_value
          })
        }

        setMembershipStatus(data)

        // 设置upgrade_options数据（无论是否已订阅）
        if (data.upgrade_options) {
          console.log('🔄 [fetchMembershipStatus] 获取到后端upgrade_options:', data.upgrade_options)
          setUpgradeOptions(data.upgrade_options)
        }
      } catch (error) {
        console.error('获取会员状态失败:', error)
        setMembershipStatus(null)
        showToast(t('toast.common.error'), 'error')
      } finally {
        setLoading(false)
      }
    }

    fetchMembershipStatus()
  }, [])

  // 显示提示消息
  const showToast = (message: string, type: 'success' | 'error') => {
    setToast({ message, type })
    setTimeout(() => setToast(null), 3000)
  }

  // 格式化日期
  const formatDate = (dateString: string) => {
    if (!dateString) return '无'

    const date = new Date(dateString)

    // 检查日期是否有效，如果是无效日期（如0001-01-01），返回"无"
    if (isNaN(date.getTime()) || date.toISOString().startsWith('0001-01-01')) {
      return '无'
    }

    return date.toISOString().split('T')[0]
  }

  // 获取会员类型名称
  const getSubscriptionTypeName = (type: number) => {
    if (!type) return t('membership.subscriptionType.none')

    const typeMap: Record<number, 'none' | 'basic' | 'pro' | 'business' | 'unknown'> = {
      1: 'basic',
      2: 'pro',
      3: 'business'
    }

    const typeKey = typeMap[type] || 'unknown'
    return t(`membership.subscriptionType.${typeKey}`)
  }

  // 创建新订阅（暂时注释，避免未使用警告）
  // const createSubscription = async (subscriptionType: number, billingCycle: number) => {
  //   try {
  //     const response = await fetch(`${API_BASE_URL}/api/subscriptions`, {
  //       method: 'POST',
  //       headers: getHeaders(),
  //       body: JSON.stringify({
  //         subscription_type: subscriptionType,
  //         billing_cycle: billingCycle,
  //         auto_renew: false
  //       })
  //     })

  //     const data = await handleResponse<any>(response)
  //     showToast('订阅创建成功！', 'success')

  //     // 刷新会员状态
  //     fetchMembershipStatus()

  //     return data
  //   } catch (error: any) {
  //     console.error('创建订阅失败:', error)
  //     showToast(error.message || '创建订阅失败，请重试', 'error')
  //     throw error
  //   }
  // }

  // 升级订阅（暂时注释，避免未使用警告）
  // const upgradeSubscription = async (subscriptionType: number, billingCycle: number) => {
  //   try {
  //     const response = await fetch(`${API_BASE_URL}/api/subscriptions/upgrade`, {
  //       method: 'POST',
  //       headers: getHeaders(),
  //       body: JSON.stringify({
  //         subscription_type: subscriptionType,
  //         billing_cycle: billingCycle
  //       })
  //     })

  //     const data = await handleResponse<any>(response)
  //     showToast('升级成功！', 'success')

  //     // 刷新会员状态
  //     fetchMembershipStatus()

  //     return data
  //   } catch (error: any) {
  //     console.error('升级订阅失败:', error)
  //     showToast(error.message || '升级失败，请重试', 'error')
  //     throw error
  //   }
  // }

  // 将后端upgrade_options转换为前端MembershipPlan格式
  const convertUpgradeOptionsToPlans = (options: UpgradeOption[]): MembershipPlan[] => {
    const planConfig = {
      1: {
        name: t('membership.subscriptionType.basic'),
        description: t('membership.descriptions.basic'),
        features: [
          '2000 credits/month',
          t('membership.features.aiGeneratedContent'),
          t('membership.features.voiceovers'),
          t('membership.features.basicEditingTools'),
          t('membership.features.emailSupport'),
          '1GB storage',
          '200 Task Histories'
        ]
      },
      2: {
        name: t('membership.subscriptionType.pro'),
        description: t('membership.descriptions.pro'),
        features: [
          '12000 credits/month',
          t('membership.features.aiGeneratedContent'),
          t('membership.features.voiceovers'),
          t('membership.features.basicEditingTools'),
          t('membership.features.emailSupport'),
          '10GB storage',
          '1000 Task Histories'
        ]
      },
      3: {
        name: t('membership.subscriptionType.business'),
        description: t('membership.descriptions.business'),
        features: [
          '40000 credits/month',
          t('membership.features.aiGeneratedContent'),
          t('membership.features.voiceovers'),
          t('membership.features.basicEditingTools'),
          t('membership.features.emailSupport'),
          '100GB storage',
          '10000 Task Histories'
        ]
      }
    }

    return options.map(option => {
      const config = planConfig[option.subscription_type as keyof typeof planConfig]
      return {
        id: option.subscription_type,
        name: config.name,
        description: config.description,
        price: (option.upgrade_price || option.original_price) / 100, // 升级用户使用升级价格，新订阅用户使用原始价格
        features: config.features,
        recommended: option.subscription_type === 2, // Pro版本标记为推荐
        originalPrice: option.original_price,
        upgradePrice: option.upgrade_price, // 保存升级价格，仅升级用户有
        billingCycle: option.billing_cycle,
        discountPercentage: option.discount_percentage,
        billingCycleName: option.billing_cycle_name,
        subscriptionTypeName: option.subscription_type_name
      }
    })
  }

  // 刷新会员状态
  const fetchMembershipStatus = async () => {
    try {
      setLoading(true)
      const response = await fetch(`${API_BASE_URL}/subscriptions`, {
        method: 'GET',
        headers: getHeaders()
      })
      const data = await handleResponse<any>(response)
      setMembershipStatus(data)

      // 更新upgrade_options（所有用户，无论是否订阅）
      if (data.upgrade_options) {
        setUpgradeOptions(data.upgrade_options)
      }

      // 保存upgrade_paths到状态
      // 注意：代码中暂时没有在JSX中展示upgradePaths，所以先不创建单独的state
      // 如果需要在UI中展示智能升级推荐，可以参考改造方案添加
    } catch (error) {
      console.error('获取会员状态失败:', error)
    } finally {
      setLoading(false)
    }
  }

  // 处理升级计划
  const handleUpgradePlan = (plan: MembershipPlan) => {
    const currentPlanType = membershipStatus?.active_subscription?.subscription_type;
    const isNewSubscription = !membershipStatus?.active_subscription;

    if (isNewSubscription) {
      // 新订阅：确保不包含任何与升级相关的字段
      const newPlan = { ...plan };
      // 移除可能存在的升级相关字段
      delete newPlan.upgradeFrom;
      delete newPlan.upgradePrice;
      setSelectedPlan({ ...newPlan, isNewSubscription: true })
    } else if (currentPlanType !== plan.id) {
      // 升级订阅
      setSelectedPlan({ ...plan, upgradeFrom: currentPlanType, isNewSubscription: false })
    } else {
      // 同类型续费：确保不包含任何与升级相关的字段
      const renewalPlan = { ...plan };
      delete renewalPlan.upgradeFrom;
      delete renewalPlan.upgradePrice;
      setSelectedPlan({ ...renewalPlan, isRenewal: true, isNewSubscription: false })
    }

    setIsPayPalDialogOpen(true)
  }

  // 处理支付成功
  const handlePaymentSuccess = async (message: string) => {
    setIsPayPalDialogOpen(false)
    showToast(message || t('toast.membership.paymentSuccess'), 'success')

    // 刷新会员状态
    await fetchMembershipStatus()
  }

  // 处理支付错误
  const handlePaymentError = (error: string) => {
    showToast(error || t('toast.membership.paymentFailed'), 'error')
  }


  return (
    <div className="max-w-7xl mx-auto px-6 py-12 pb-[100px] overflow-x-hidden">

      {/* Current Membership Status */}
      <div className="mb-10 p-6 bg-gradient-to-r from-gray-900/95 via-blue-500/10 to-gray-900/95 border border-blue-500/50 rounded-2xl backdrop-blur-md">
        {loading ? (
          <div className="flex justify-center items-center py-8">
            <div className="w-10 h-10 border-4 border-blue-500/50 border-t-blue-500 rounded-full animate-spin"></div>
            <span className="ml-3 text-gray-400">{t('membership.loadingStatus')}</span>
          </div>
        ) : membershipStatus ? (
          <>
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4">
              <div>
                <h3 className="text-xl font-bold mb-1">{t('membership.currentStatus')}</h3>
                <p className="text-sm text-gray-400">{t('membership.usageInfo')}</p>
              </div>
              <div className="mt-3 md:mt-0 px-4 py-1 bg-blue-500/20 rounded-full text-sm font-medium">
                <span className="text-blue-400">
                  {getSubscriptionTypeName(membershipStatus?.active_subscription?.subscription_type || 0)}{t('membership.member')}
                </span>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* 积分权益显示 */}
              {membershipStatus?.active_subscription?.benefit_periods?.find((bp: any) => bp.benefit_code === 'credits') && (
                <div className="bg-gray-800/80 rounded-xl p-3">
                  <div className="flex justify-between items-center">
                    <div>
                      <div className="text-xs text-gray-400 mb-1">{t('membership.monthlyRemaining')}</div>
                      <div className="text-lg font-semibold">
                        {(() => {
                          const creditsBenefit = membershipStatus?.active_subscription?.benefit_periods?.find((bp: any) => bp.benefit_code === 'credits');
                          if (!creditsBenefit) return '0/0';

                          const remainingValue = creditsBenefit.remaining_value !== undefined
                            ? creditsBenefit.remaining_value
                            : (creditsBenefit.total_value - creditsBenefit.used_value);

                          return `${remainingValue}/${creditsBenefit.total_value || 0}`;
                        })()}
                      </div>
                      <div className="text-xs text-gray-500">{t('membership.credits')}</div>
                    </div>
                    <div className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center">
                      <span className="text-green-400 text-sm">✓</span>
                    </div>
                  </div>
                </div>
              )}

              {/* 有效期显示 */}
              <div className="bg-gray-800/80 rounded-xl p-3">
                <div className="flex justify-between items-center">
                  <div>
                    <div className="text-xs text-gray-400 mb-1">{t('membership.validUntil')}</div>
                    <div className="text-lg font-semibold">
                      {formatDate(membershipStatus?.active_subscription?.end_date || '')}
                    </div>
                    <div className="text-xs text-gray-500">
                      {(() => {
                        const endDate = membershipStatus?.active_subscription?.end_date ? new Date(membershipStatus.active_subscription.end_date) : null;
                        const today = new Date();
                        const daysRemaining = endDate ? Math.ceil((endDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)) : 0;
                        return daysRemaining > 0 ? `${t('membership.daysRemaining', { days: daysRemaining })}` : t('membership.expired');
                      })()}
                    </div>
                  </div>
                  <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center">
                    <span className="text-blue-400 text-sm">📅</span>
                  </div>
                </div>
              </div>

              {/* 自动续费显示 */}
              <div className="bg-gray-800/80 rounded-xl p-3">
                <div className="flex justify-between items-center">
                  <div>
                    <div className="text-xs text-gray-400 mb-1">{t('membership.autoRenewal')}</div>
                    <div className="text-lg font-semibold">
                      {membershipStatus?.active_subscription?.auto_renew ? t('membership.enabled') : t('membership.disabled')}
                    </div>
                    <div className="text-xs text-gray-500">
                      {membershipStatus?.active_subscription?.auto_renew ? t('membership.autoRenewalConvenient') : t('membership.manualRenewalFlexible')}
                    </div>
                  </div>
                  <div className="w-8 h-8 rounded-full bg-orange-500/20 flex items-center justify-center">
                    <span className="text-orange-400 text-sm">⚠️</span>
                  </div>
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="py-8 text-center">
            <p className="text-gray-400 mb-4">{t('membership.noMembership')}</p>
            <p className="text-xs text-gray-500">{t('membership.pleaseLogin')}</p>
          </div>
        )}
      </div>

      {/* Available Plans */}
      <div className="mb-6">
        <h2 className="text-lg font-semibold">{t('membership.choosePlan')}</h2>
        <p className="text-gray-500 mt-2">{t('membership.upgradeToUnlock')}</p>
      </div>

      {/* Tab Navigation */}
      <div className="mb-8 flex space-x-4 border-b border-gray-800">
        <button
          className={`py-3 px-5 border-b-2 font-medium transition-colors ${activeTab === 'monthly'
            ? 'border-blue-500 text-blue-500'
            : 'border-transparent text-gray-400 hover:text-gray-300'
            }`}
          onClick={() => setActiveTab('monthly')}
        >
          {t('membership.monthly')}
        </button>
        <button
          className={`py-3 px-5 border-b-2 font-medium transition-colors ${activeTab === 'yearly'
            ? 'border-blue-500 text-blue-500'
            : 'border-transparent text-gray-400 hover:text-gray-300'
            }`}
          onClick={() => setActiveTab('yearly')}
        >
          {t('membership.yearly')}
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {membershipStatus?.active_subscription ? (
          // 如果有活跃订阅，使用后端upgrade_options数据
          <>
            {(() => {
              const billingCycle = activeTab === 'monthly' ? 1 : 12
              const upgradeOptions = membershipStatus?.upgrade_options || []

              if (upgradeOptions.length > 0) {
                // 使用后端返回的upgrade_options
                const filteredPlans = convertUpgradeOptionsToPlans(upgradeOptions).filter(plan => plan.billingCycle === billingCycle)

                return filteredPlans.map((plan) => (
                  <div
                    key={`${plan.id}-${plan.billingCycle}`}
                    className={`${plan.recommended
                      ? 'bg-gradient-to-b from-gray-900/95 via-gray-850/95 to-gray-800/95 backdrop-blur-md rounded-2xl p-6 border-2 border-blue-500/50 hover:shadow-xl transition-all duration-300 flex flex-col'
                      : 'bg-gray-800/95 backdrop-blur-md rounded-2xl p-6 border border-gray-700/50 hover:border-blue-500/50 transition-all duration-300 flex flex-col'
                      }`}
                  >
                    <div className="flex justify-between items-center mb-4">
                      <div>
                        <div className="text-xl font-bold">{plan.name}</div>
                        <div className={`text-sm mt-1 ${plan.recommended ? 'text-blue-500' : 'text-gray-400'}`}>
                          {plan.description}
                        </div>
                        {/* 显示折扣信息 */}
                        {plan.discountPercentage && plan.discountPercentage > 0 && (
                          <div className="text-xs text-green-500 mt-1">
                            {plan.billingCycleName} - {t('membership.saveMoney')}{plan.discountPercentage}%
                          </div>
                        )}
                      </div>
                      <div className="text-right">
                        <div className="text-xl font-bold">
                          ${plan.price}
                        </div>
                        {/* 显示原始价格和升级差价 - 仅升级用户 */}
                        {plan.upgradePrice && plan.originalPrice && plan.originalPrice > plan.upgradePrice && (
                          <div className="text-xs text-gray-500 mt-1 line-through">
                            ${plan.originalPrice / 100}
                          </div>
                        )}
                        <div className="text-xs text-gray-400 mt-1">
                          {plan.billingCycleName}
                        </div>
                      </div>
                    </div>
                    <div className="space-y-3 mb-6 flex-grow">
                      {plan.features.map((feature: string, index: number) => (
                        <div key={index} className="flex items-center text-sm">
                          <span className="text-green-500 mr-2">✓</span>
                          <span>{feature}</span>
                        </div>
                      ))}
                    </div>
                    {/* 检查当前套餐是否是该计划 */}
                    {plan.id === membershipStatus?.active_subscription?.subscription_type ? (
                      <button
                        disabled
                        className="w-full bg-gray-600 rounded-lg py-3 font-medium cursor-not-allowed opacity-70"
                      >
                        {t('membership.currentPlan')}
                      </button>
                    ) : (
                      <button
                        onClick={() => handleUpgradePlan(plan)}
                        className="w-full bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg px-4 py-3 font-medium transition-all duration-300 shadow-lg hover:shadow-xl text-white"
                      >
                        {t('membership.upgradeTo', { planName: plan.name })}
                      </button>
                    )}
                  </div>
                ))
              } else {
                // 没有upgrade_options，可能已是最高级会员，显示当前计划
                return (
                  <div className="col-span-1 md:col-span-3 text-center py-8">
                    <p className="text-gray-400 mb-4">{t('membership.highestMembership')}</p>
                    <p className="text-sm text-gray-500">{t('membership.currentBenefits')}：{getSubscriptionTypeName(membershipStatus?.active_subscription?.subscription_type || 0)}</p>
                  </div>
                )
              }
            })()}
          </>
        ) : (
          // 没有活跃订阅，使用后端upgrade_options显示计划
          <>
            {/* 按计费周期过滤显示计划 */}
            {(() => {
              const billingCycle = activeTab === 'monthly' ? 1 : 12
              const filteredPlans = upgradeOptions.length > 0
                ? convertUpgradeOptionsToPlans(upgradeOptions).filter(plan => plan.billingCycle === billingCycle)
                : membershipPlans

              return filteredPlans.map((plan) => (
                <div
                  key={`${plan.id}-${plan.billingCycle}`}
                  className={`${plan.recommended
                    ? 'bg-gradient-to-b from-gray-900/95 via-gray-850/95 to-gray-800/95 backdrop-blur-md rounded-2xl p-6 border-2 border-blue-500/50 hover:shadow-xl transition-all duration-300 flex flex-col'
                    : 'bg-gray-800/95 backdrop-blur-md rounded-2xl p-6 border border-gray-700/50 hover:border-blue-500/50 transition-all duration-300 flex flex-col'
                    }`}
                >
                  <div className="flex justify-between items-center mb-4">
                    <div>
                      <div className="text-xl font-bold">{plan.name}</div>
                      <div className={`text-sm mt-1 ${plan.recommended ? 'text-blue-500' : 'text-gray-400'}`}>
                        {plan.description}
                      </div>
                      {/* 显示折扣信息 */}
                      {plan.discountPercentage && plan.discountPercentage > 0 && (
                        <div className="text-xs text-green-500 mt-1">
                          {plan.billingCycleName} - {t('membership.saveMoney')}{plan.discountPercentage}%
                        </div>
                      )}
                    </div>
                    <div className="text-right">
                      <div className="text-xl font-bold">
                        ${plan.price}
                      </div>
                      <div className="text-xs text-gray-400 mt-1">
                        {plan.billingCycleName}
                      </div>
                    </div>
                  </div>
                  <div className="space-y-3 mb-6 flex-grow">
                    {plan.features.map((feature: string, index: number) => (
                      <div key={index} className="flex items-center text-sm">
                        <span className="text-green-500 mr-2">✓</span>
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>
                  {/* 所有套餐都可以直接购买 */}
                  <button
                    onClick={() => handleUpgradePlan(plan)}
                    className="w-full bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg px-4 py-3 font-medium transition-all duration-300 shadow-lg hover:shadow-xl text-white"
                  >
                    {t('membership.getPlan', { planName: plan.name })}
                  </button>
                </div>
              ))
            })()}
          </>
        )}
      </div>

      {/* 支付方式展示 */}
      {paymentMethods.length > 0 && (
        <div className="mt-12 text-center">
          <h3 className="text-lg font-semibold mb-4">{t('membership.supportedPaymentMethods')}</h3>
          <div className="flex justify-center space-x-6">
            {paymentMethods.map((method, index) => (
              <div key={index} className="text-sm text-gray-400">
                {method}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* PayPal支付对话框 */}
      <PayPalPaymentDialog
        isOpen={isPayPalDialogOpen}
        onClose={() => setIsPayPalDialogOpen(false)}
        plan={selectedPlan!}
        billingCycle={activeTab === 'monthly' ? 1 : 12} // 根据当前选中的标签页确定计费周期
        onSuccess={handlePaymentSuccess}
        onError={handlePaymentError}
      />

      {/* Toast 提示 */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  )
}

export default MembershipPage